import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ClienteFormValues, getDynamicClienteFormSchema } from "../components/ClienteFormSchema";
import { useClienteTypeDetection } from "./useClienteTypeDetection";
import { Cliente, EstadoCivil, TipoConta } from "../types/cliente";
import { ClientesService } from "../services/clientes.service";
import { format } from "date-fns";
import { useAuth } from "@/features/auth";
import { useAutoPreenchimento } from "@/hooks/useAutoPreenchimento";
import { useValidacaoProgressiva } from "@/hooks/useValidacaoProgressiva";
import { useProfileSwitcher } from "@/hooks/useProfileSwitcher";

export const useClienteForm = (formData?: Cliente, onSuccess?: (cliente: Cliente) => void) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { getCurrentProfile } = useProfileSwitcher();
  const { buscarPorCPF, dadosCliente, loading: loadingCPF } = useAutoPreenchimento();
  const { validarCampo, erros, sugestoes } = useValidacaoProgressiva();

  // Obter perfil atual (real ou simulado)
  const currentRole = getCurrentProfile;


  // Detectar tipo de cliente baseado no CPF/CNPJ
  const cpfValue = formData?.cpf || '';
  const { clienteType } = useClienteTypeDetection({ cpfCnpj: cpfValue });

  // Usar schema dinâmico baseado no tipo de cliente
  const dynamicSchema = getDynamicClienteFormSchema(clienteType);

  const form = useForm<ClienteFormValues>({
    resolver: zodResolver(dynamicSchema),
    defaultValues: {
      nome: formData?.nome || "",
      email: formData?.email || "",
      tel1: formData?.tel1 || "",
      tel2: formData?.tel2 || "",
      dataNascimento: formData?.data_nascimento ? new Date(formData.data_nascimento) : undefined,
      cpf: formData?.cpf || "",
      rg: formData?.rg || "",
      dataEmissaoRg: formData?.data_emissao_rg ? new Date(formData.data_emissao_rg) : undefined,
      // Campos para PJ
      razaoSocial: formData?.razao_social || "",
      inscricaoEstadual: formData?.inscricao_estadual || "",
      nomeResponsavel: formData?.nome_responsavel || "",
      cpfResponsavel: formData?.cpf_responsavel || "",
      emailResponsavel: formData?.email_responsavel || "",
      telResponsavel: formData?.tel_responsavel || "",
      // Campos gerais
      naturalidade: formData?.naturalidade || "",
      nacionalidade: formData?.nacionalidade || "Brasileira",
      atividadeDesenvolvida: formData?.atividade_desenvolvida || "",
      rendimentoTitular: formData?.rendimento_titular ? (typeof formData.rendimento_titular === 'string' ? parseFloat(formData.rendimento_titular) : formData.rendimento_titular) : undefined,
      logradouro: formData?.logradouro || "",
      numero: formData?.numero || "",
      complemento: formData?.complemento || "",
      bairro: formData?.bairro || "",
      cep: formData?.cep || "",
      cidade: formData?.cidade || "",
      estado: formData?.estado || "",
      nomeMae: formData?.nome_mae || "",
      nomePai: formData?.nome_pai || "",
      estadoCivil: (formData?.estado_civil as EstadoCivil) || "solteiro",
      nomeConjuge: formData?.nome_conjuge || "",
      cpfConjuge: formData?.cpf_conjuge || "",
      dataNascimentoConjuge: formData?.data_nascimento_conjuge ? new Date(formData.data_nascimento_conjuge) : undefined,
      tipoConta: (formData?.tipo_conta as TipoConta) || "corrente",
      banco: formData?.banco || "",
      agencia: formData?.agencia || "",
      conta: formData?.conta || "",
      statusAtual: formData?.status_atual || "",
      // Campos de Qualificação
      temEntrada: formData?.tem_entrada || false,
      valorEntrada: formData?.valor_entrada ? (typeof formData.valor_entrada === 'string' ? parseFloat(formData.valor_entrada) : formData.valor_entrada) : undefined,
      valorParcelaOrcamento: formData?.valor_parcela_orcamento ? (typeof formData.valor_parcela_orcamento === 'string' ? parseFloat(formData.valor_parcela_orcamento) : formData.valor_parcela_orcamento) : undefined,
      tempoProcura: formData?.tempo_procura as any || undefined,
      prioridade: formData?.prioridade as any || undefined,
      tempoAluguel: formData?.tempo_aluguel || "",
      tipoBem: (formData?.tipo_bem as any) || "IMOVEL",
    },
    mode: "onChange",
  });

  const processDate = (date: Date | string | undefined) => {
    if (!date) return undefined;

    if (typeof date === 'string') {
      // Se já é uma string no formato yyyy-MM-dd, retorna como está
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }
      // Se é uma string de data válida, converte para o formato correto
      const parsedDate = new Date(date);
      if (!isNaN(parsedDate.getTime())) {
        return format(parsedDate, 'yyyy-MM-dd');
      }
      return undefined;
    }

    if (date instanceof Date) {
      return format(date, 'yyyy-MM-dd');
    }

    return undefined;
  };

  // Mutação para salvar como rascunho
  const { mutate: saveDraftMutation, isPending: isSavingDraft } = useMutation({
    mutationFn: async (data: ClienteFormValues) => {
      try {
        const tenantId = formData?.tenant_id || profile?.tenant_id;

        if (!tenantId) {
          throw new Error("Tenant ID não encontrado. Verifique se você está vinculado a uma empresa.");
        }


        // Garantir que o created_by seja preenchido
        const createdBy = profile?.uuid || profile?.id;
        if (!createdBy) {
          throw new Error("Usuário não identificado. Faça login novamente.");
        }

        const clienteData = {
          tenant_id: tenantId.toString(), // Sempre incluir tenant_id
          created_by: createdBy, // Sempre incluir created_by
          nome: data.nome,
          email: data.email,
          tel1: data.tel1,
          tel2: data.tel2 || undefined,
          data_nascimento: processDate(data.dataNascimento),
          cpf: data.cpf,
          rg: data.rg,
          data_emissao_rg: processDate(data.dataEmissaoRg),
          // Campos para PJ
          razao_social: data.razaoSocial || "",
          inscricao_estadual: data.inscricaoEstadual || "",
          nome_responsavel: data.nomeResponsavel || "",
          cpf_responsavel: data.cpfResponsavel || "",
          email_responsavel: data.emailResponsavel || "",
          tel_responsavel: data.telResponsavel || "",
          // Campos gerais
          naturalidade: data.naturalidade || "",
          nacionalidade: data.nacionalidade || "Brasileira",
          atividade_desenvolvida: data.atividadeDesenvolvida || "",
          rendimento_titular: data.rendimentoTitular ? data.rendimentoTitular.toString() : "",
          logradouro: data.logradouro || "",
          numero: data.numero || "",
          complemento: data.complemento || "",
          bairro: data.bairro || "",
          cep: data.cep || "",
          cidade: data.cidade || "",
          estado: data.estado || "",
          nome_mae: data.nomeMae || "",
          nome_pai: data.nomePai || "",
          estado_civil: data.estadoCivil || "solteiro",
          nome_conjuge: data.nomeConjuge || "",
          cpf_conjuge: data.cpfConjuge || "",
          data_nascimento_conjuge: processDate(data.dataNascimentoConjuge),
          tipo_conta: data.tipoConta || "corrente",
          banco: data.banco || "",
          agencia: data.agencia || "",
          conta: data.conta || "",
          status_atual: data.statusAtual || "",
          // Campos de Qualificação
          tem_entrada: data.temEntrada || false,
          valor_entrada: data.valorEntrada ? data.valorEntrada.toString() : undefined,
          valor_parcela_orcamento: data.valorParcelaOrcamento ? data.valorParcelaOrcamento.toString() : undefined,
          tempo_procura: data.tempoProcura || undefined,
          prioridade: data.prioridade || undefined,
          tempo_aluguel: data.tempoAluguel || undefined,
          tipo_bem: data.tipoBem,
          status: 'draft' as const // Definir status como rascunho
        };


        let savedCliente: Cliente;

        if (formData?.id) {
          savedCliente = await ClientesService.atualizarCliente(formData.id.toString(), clienteData as any);
        } else {
          savedCliente = await ClientesService.criarCliente(clienteData as any);
        }

        // Upload dos documentos para rascunho também
        if (data.documentoComFotoDoc instanceof FileList && data.documentoComFotoDoc.length > 0) {
          await ClientesService.uploadDocumento(
            savedCliente.id.toString(),
            data.documentoComFotoDoc[0],
            'documento_com_foto',
            tenantId.toString(),
            createdBy.toString()
          );
        }

        if (data.residenciaDoc instanceof FileList && data.residenciaDoc.length > 0) {
          await ClientesService.uploadDocumento(
            savedCliente.id.toString(),
            data.residenciaDoc[0],
            'comprovante_residencia',
            tenantId.toString(),
            createdBy.toString()
          );
        }

        if (data.comprovantePagamentoDoc instanceof FileList && data.comprovantePagamentoDoc.length > 0) {
          await ClientesService.uploadDocumento(
            savedCliente.id.toString(),
            data.comprovantePagamentoDoc[0],
            'comprovante_pagamento',
            tenantId.toString(),
            createdBy.toString()
          );
        }

        if (data.declaracoesDoc instanceof FileList && data.declaracoesDoc.length > 0) {
          await ClientesService.uploadDocumento(
            savedCliente.id.toString(),
            data.declaracoesDoc[0],
            'declaracoes',
            tenantId.toString(),
            createdBy.toString()
          );
        }

        if (data.comprovanteRendaDoc instanceof FileList && data.comprovanteRendaDoc.length > 0) {
          await ClientesService.uploadDocumento(
            savedCliente.id.toString(),
            data.comprovanteRendaDoc[0],
            'comprovante_renda',
            tenantId.toString(),
            createdBy.toString()
          );
        }

        return savedCliente;
      } catch (error: any) {
        console.error("Erro ao salvar rascunho:", error);
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["clientes"] });
      queryClient.invalidateQueries({ queryKey: ["clientes-unified"] });
      queryClient.invalidateQueries({ queryKey: ["documentos", data.id] });

      toast.success(`Rascunho salvo com sucesso! Cliente: ${data.nome}`);
    },
    onError: (error: any) => {
      console.error("Erro ao salvar rascunho:", error);

      if (error.response?.status === 409) {
        toast.error("CPF já cadastrado no sistema. Verifique se o cliente já existe.");
      } else {
        toast.error(error.message || "Erro ao salvar rascunho");
      }
    }
  });

  // Mutação para salvar e criar contrato
  const { mutate: saveAndCreateContractMutation, isPending: isSavingAndCreating } = useMutation({
    mutationFn: async (data: ClienteFormValues) => {
      try {
        const tenantId = formData?.tenant_id || profile?.tenant_id;

        if (!tenantId) {
          throw new Error("Tenant ID não encontrado. Verifique se você está vinculado a uma empresa.");
        }

        // Garantir que o created_by seja preenchido
        const createdBy = profile?.uuid || profile?.id;
        if (!createdBy) {
          throw new Error("Usuário não identificado. Faça login novamente.");
        }

        const clienteData = {
          tenant_id: tenantId.toString(),
          created_by: createdBy,
          nome: data.nome,
          email: data.email,
          tel1: data.tel1,
          tel2: data.tel2 || undefined,
          data_nascimento: processDate(data.dataNascimento),
          cpf: data.cpf,
          rg: data.rg,
          data_emissao_rg: processDate(data.dataEmissaoRg),
          // Campos para PJ
          razao_social: data.razaoSocial || "",
          inscricao_estadual: data.inscricaoEstadual || "",
          nome_responsavel: data.nomeResponsavel || "",
          cpf_responsavel: data.cpfResponsavel || "",
          email_responsavel: data.emailResponsavel || "",
          tel_responsavel: data.telResponsavel || "",
          // Campos gerais
          naturalidade: data.naturalidade || "",
          nacionalidade: data.nacionalidade || "Brasileira",
          atividade_desenvolvida: data.atividadeDesenvolvida || "",
          rendimento_titular: data.rendimentoTitular ? data.rendimentoTitular.toString() : "",
          logradouro: data.logradouro || "",
          numero: data.numero || "",
          complemento: data.complemento || "",
          bairro: data.bairro || "",
          cep: data.cep || "",
          cidade: data.cidade || "",
          estado: data.estado || "",
          nome_mae: data.nomeMae || "",
          nome_pai: data.nomePai || "",
          estado_civil: data.estadoCivil || "solteiro",
          nome_conjuge: data.nomeConjuge || "",
          cpf_conjuge: data.cpfConjuge || "",
          data_nascimento_conjuge: processDate(data.dataNascimentoConjuge),
          tipo_conta: data.tipoConta || "corrente",
          banco: data.banco || "",
          agencia: data.agencia || "",
          conta: data.conta || "",
          status_atual: data.statusAtual || "",
          // Campos de Qualificação
          tem_entrada: data.temEntrada || false,
          valor_entrada: data.valorEntrada ? data.valorEntrada.toString() : undefined,
          valor_parcela_orcamento: data.valorParcelaOrcamento ? data.valorParcelaOrcamento.toString() : undefined,
          tempo_procura: data.tempoProcura || undefined,
          prioridade: data.prioridade || undefined,
          tempo_aluguel: data.tempoAluguel || undefined,
          tipo_bem: data.tipoBem,
          status: 'ativo' as const // Definir status como ativo para salvamento definitivo
        };


        let savedCliente: Cliente;

        if (formData?.id) {
          savedCliente = await ClientesService.atualizarCliente(formData.id.toString(), clienteData as any);
        } else {
          savedCliente = await ClientesService.criarCliente(clienteData as any);
        }

        // Upload dos documentos
        if (data.documentoComFotoDoc instanceof FileList && data.documentoComFotoDoc.length > 0) {
          await ClientesService.uploadDocumento(
            savedCliente.id.toString(),
            data.documentoComFotoDoc[0],
            'documento_com_foto',
            tenantId.toString(),
            createdBy.toString()
          );
        }

        if (data.residenciaDoc instanceof FileList && data.residenciaDoc.length > 0) {
          await ClientesService.uploadDocumento(
            savedCliente.id.toString(),
            data.residenciaDoc[0],
            'comprovante_residencia',
            tenantId.toString(),
            createdBy.toString()
          );
        }

        if (data.comprovantePagamentoDoc instanceof FileList && data.comprovantePagamentoDoc.length > 0) {
          await ClientesService.uploadDocumento(
            savedCliente.id.toString(),
            data.comprovantePagamentoDoc[0],
            'comprovante_pagamento',
            tenantId.toString(),
            createdBy.toString()
          );
        }

        if (data.declaracoesDoc instanceof FileList && data.declaracoesDoc.length > 0) {
          await ClientesService.uploadDocumento(
            savedCliente.id.toString(),
            data.declaracoesDoc[0],
            'declaracoes',
            tenantId.toString(),
            createdBy.toString()
          );
        }

        if (data.comprovanteRendaDoc instanceof FileList && data.comprovanteRendaDoc.length > 0) {
          await ClientesService.uploadDocumento(
            savedCliente.id.toString(),
            data.comprovanteRendaDoc[0],
            'comprovante_renda',
            tenantId.toString(),
            createdBy.toString()
          );
        }

        return savedCliente;
      } catch (error: any) {
        console.error("Erro ao salvar cliente para criar contrato:", error);
        throw error;
      }
    },
    onSuccess: (savedCliente) => {
      // Invalidar queries relacionadas a clientes
      queryClient.invalidateQueries({ queryKey: ["clientes"] });
      queryClient.invalidateQueries({ queryKey: ["clientes-unified"] });
      queryClient.invalidateQueries({ queryKey: ["documentos", savedCliente.id] });

      toast.success(`Cliente ${savedCliente.nome} salvo com sucesso! Redirecionando para criação de contrato...`);

      // Navegar para criação de contrato com o ID do cliente
      navigate(`/contratos/novo?cliente_id=${savedCliente.id}&cliente_nome=${encodeURIComponent(savedCliente.nome)}`);
    },
    onError: (error: any) => {
      console.error("Erro ao salvar cliente para criar contrato:", error);

      if (error.response?.status === 409) {
        toast.error("CPF já cadastrado no sistema. Verifique se o cliente já existe.");
      } else {
        toast.error(error.message || "Erro ao salvar cliente");
      }
    }
  });

  const { mutate: submitForm, isPending: isSubmitting } = useMutation({
    mutationFn: async (data: ClienteFormValues) => {
      try {
        const tenantId = formData?.tenant_id || profile?.tenant_id;

        if (!tenantId) {
          throw new Error("Tenant ID não encontrado. Verifique se você está vinculado a uma empresa.");
        }



        // Garantir que o created_by seja preenchido
        const createdBy = profile?.uuid || profile?.id;
        if (!createdBy) {
          throw new Error("Usuário não identificado. Faça login novamente.");
        }

        const clienteData = {
          tenant_id: tenantId.toString(), // Sempre incluir tenant_id
          created_by: createdBy, // Sempre incluir created_by
          nome: data.nome,
          email: data.email,
          tel1: data.tel1,
          tel2: data.tel2 || undefined,
          data_nascimento: processDate(data.dataNascimento),
          cpf: data.cpf,
          rg: data.rg,
          data_emissao_rg: processDate(data.dataEmissaoRg),
          // Campos para PJ
          razao_social: data.razaoSocial || "",
          inscricao_estadual: data.inscricaoEstadual || "",
          nome_responsavel: data.nomeResponsavel || "",
          cpf_responsavel: data.cpfResponsavel || "",
          email_responsavel: data.emailResponsavel || "",
          tel_responsavel: data.telResponsavel || "",
          // Campos gerais
          naturalidade: data.naturalidade || "",
          nacionalidade: data.nacionalidade || "Brasileira",
          atividade_desenvolvida: data.atividadeDesenvolvida || "",
          rendimento_titular: data.rendimentoTitular ? data.rendimentoTitular.toString() : "",
          logradouro: data.logradouro || "",
          numero: data.numero || "",
          complemento: data.complemento || "",
          bairro: data.bairro || "",
          cep: data.cep || "",
          cidade: data.cidade || "",
          estado: data.estado || "",
          nome_mae: data.nomeMae || "",
          nome_pai: data.nomePai || "",
          estado_civil: data.estadoCivil || "solteiro",
          nome_conjuge: data.nomeConjuge || "",
          cpf_conjuge: data.cpfConjuge || "",
          data_nascimento_conjuge: processDate(data.dataNascimentoConjuge),
          tipo_conta: data.tipoConta || "corrente",
          banco: data.banco || "",
          agencia: data.agencia || "",
          conta: data.conta || "",
          status_atual: data.statusAtual || "",
          // Campos de Qualificação
          tem_entrada: data.temEntrada || false,
          valor_entrada: data.valorEntrada ? data.valorEntrada.toString() : undefined,
          valor_parcela_orcamento: data.valorParcelaOrcamento ? data.valorParcelaOrcamento.toString() : undefined,
          tempo_procura: data.tempoProcura || undefined,
          prioridade: data.prioridade || undefined,
          tempo_aluguel: data.tempoAluguel || undefined,
          tipo_bem: data.tipoBem,
          status: 'ativo' as const // Definir status como ativo para salvamento definitivo
        };


        let savedCliente: Cliente;

        if (formData?.id) {
          savedCliente = await ClientesService.atualizarCliente(formData.id.toString(), clienteData as any);
        } else {
          savedCliente = await ClientesService.criarCliente(clienteData as any);
        }

        // Upload dos documentos
        if (data.documentoComFotoDoc instanceof FileList && data.documentoComFotoDoc.length > 0) {
          await ClientesService.uploadDocumento(
            savedCliente.id.toString(),
            data.documentoComFotoDoc[0],
            'documento_com_foto'
          );
        }

        if (data.residenciaDoc instanceof FileList && data.residenciaDoc.length > 0) {
          await ClientesService.uploadDocumento(
            savedCliente.id.toString(),
            data.residenciaDoc[0],
            'comprovante_residencia',
            tenantId.toString(),
            createdBy.toString()
          );
        }

        if (data.comprovantePagamentoDoc instanceof FileList && data.comprovantePagamentoDoc.length > 0) {
          await ClientesService.uploadDocumento(
            savedCliente.id.toString(),
            data.comprovantePagamentoDoc[0],
            'comprovante_pagamento',
            tenantId.toString(),
            createdBy.toString()
          );
        }

        if (data.declaracoesDoc instanceof FileList && data.declaracoesDoc.length > 0) {
          await ClientesService.uploadDocumento(
            savedCliente.id.toString(),
            data.declaracoesDoc[0],
            'declaracoes',
            tenantId.toString(),
            createdBy.toString()
          );
        }

        if (data.comprovanteRendaDoc instanceof FileList && data.comprovanteRendaDoc.length > 0) {
          await ClientesService.uploadDocumento(
            savedCliente.id.toString(),
            data.comprovanteRendaDoc[0],
            'comprovante_renda',
            tenantId.toString(),
            createdBy.toString()
          );
        }

        return savedCliente;
      } catch (error: any) {
        console.error("Erro ao salvar cliente:", error);
        throw error;
      }
    },
    onSuccess: (data) => {
      // Invalidar todas as queries relacionadas a clientes
      queryClient.invalidateQueries({ queryKey: ["clientes"] });
      queryClient.invalidateQueries({ queryKey: ["documentos", data.id] });

      // Invalidar também a query específica do cliente se estiver editando
      if (formData?.id) {
        queryClient.invalidateQueries({ queryKey: ["cliente", formData.id] });
        queryClient.invalidateQueries({ queryKey: ["cliente", data.id] });
      }

      // Invalidar queries que podem conter dados do cliente
      queryClient.invalidateQueries({ queryKey: ["cliente"] });

      const message = formData?.id ? "Cliente atualizado com sucesso!" : "Cliente cadastrado com sucesso!";
      toast.success(message);
      
      if (onSuccess) {
        onSuccess(data);
      } else {
        navigate("/clientes");
      }
    },
    onError: (error: any) => {
      console.error("Erro ao salvar cliente:", error);

      // Tratar erros específicos
      if (error.response?.status === 409) {
        toast.error("CPF já cadastrado no sistema. Verifique se o cliente já existe.");
      } else {
        toast.error(error.message || "Erro ao salvar cliente");
      }
    }
  });

  const onSubmit = async (data: ClienteFormValues) => {

    // Verificar se há erros antes de continuar
    const hasErrors = Object.keys(form.formState.errors).length > 0;
    if (hasErrors) {
      console.error('⚠️ Formulário tem erros de validação, abortando submissão');
      toast.error('Por favor, corrija os erros no formulário antes de salvar.');
      return;
    }

    // Verificar se o CPF já existe antes de tentar criar (apenas para novos clientes)
    if (!formData?.id && data.cpf) {
      const cleanCPF = data.cpf.replace(/\D/g, '');
      try {
        const cpfExists = await ClientesService.verificarCPFExistente(cleanCPF, formData?.id?.toString());
        if (cpfExists) {
          toast.error("CPF já cadastrado no sistema. Verifique se o cliente já existe.");
          return;
        }
      } catch (error) {
        // Se erro na verificação, continua com o envio (o backend validará)
        console.warn("Erro ao verificar CPF, continuando com envio:", error);
      }
    }


    submitForm(data);
  };

  // Função para salvar como rascunho
  const onSaveDraft = async (data: ClienteFormValues) => {
    saveDraftMutation(data);
  };

  // Função para salvar e criar contrato
  const onSaveAndCreateContract = async (data: ClienteFormValues) => {

    // Verificar se o CPF/CNPJ já existe antes de tentar criar (apenas para novos clientes)
    if (!formData?.id && data.cpf) {
      const cleanCPF = data.cpf.replace(/\D/g, '');
      try {
        const cpfExists = await ClientesService.verificarCPFExistente(cleanCPF, formData?.id?.toString());
        if (cpfExists) {
          const docType = cleanCPF.length === 11 ? 'CPF' : 'CNPJ';
          toast.error(`${docType} já cadastrado no sistema. Verifique se o cliente já existe.`);
          return;
        }
      } catch (error) {
        // Se erro na verificação, continua com o envio (o backend validará)
        console.warn("Erro ao verificar CPF/CNPJ, continuando com envio:", error);
      }
    }

    saveAndCreateContractMutation(data);
  };

  // Função para buscar cliente por CPF/CNPJ e preencher formulário
  const handleCPFSearch = async (documento: string) => {
    // Aceita CPF (11) ou CNPJ (14)
    if (!documento || (documento.length < 11 && documento.length < 14)) return;

    try {
      const cliente = await buscarPorCPF(documento);
      if (cliente) {
        // Detectar tipo de documento para atualizar campos corretos
        const documentoLimpo = documento.replace(/\D/g, '');
        const isCNPJ = documentoLimpo.length === 14;

        // Primeiro, atualizar o campo CPF/CNPJ para que o tipo seja detectado
        form.setValue('cpf', cliente.cpf || '');

        // Aguardar um momento para o tipo ser detectado
        await new Promise(resolve => setTimeout(resolve, 100));

        // Preencher campos básicos
        form.setValue('nome', cliente.nome || '');
        form.setValue('email', cliente.email || '');
        form.setValue('tel1', cliente.tel1 || '');
        form.setValue('cidade', cliente.cidade || '');
        form.setValue('estado', cliente.estado || '');
        form.setValue('cep', cliente.cep || '');

        // Se for CNPJ, preencher campos específicos de PJ
        if (isCNPJ && cliente.razaoSocial) {
          form.setValue('razaoSocial', cliente.razaoSocial || '');
          form.setValue('inscricaoEstadual', cliente.inscricaoEstadual || '');
          form.setValue('nomeResponsavel', cliente.nomeResponsavel || '');
          form.setValue('cpfResponsavel', cliente.cpfResponsavel || '');
          form.setValue('emailResponsavel', cliente.emailResponsavel || '');
          form.setValue('telResponsavel', cliente.telResponsavel || '');
        }

        return cliente;
      }
    } catch (error) {
      console.error('Erro ao buscar cliente por documento:', error);
    }
    return null;
  };

  // Função para validar campo em tempo real
  const handleFieldValidation = async (fieldName: string, value: any) => {
    return await validarCampo(fieldName, value);
  };

  // Configuração específica por papel
  const getFormConfig = () => {
    switch (currentRole) {
      case 'LIDER_VENDAS':
        return {
          showOptionalFields: true,
          enableQuickActions: true,
          focusOnEssentials: false,
          showProgressBar: true,
          title: "Novo Cliente - Líder de Vendas",
          description: "Cadastro completo de dados e documentos"
        };
      case 'RELACIONADOR':
        return {
          showOptionalFields: false,
          enableQuickActions: true,
          focusOnEssentials: true,
          showProgressBar: true,
          title: "Novo Cliente - Relacionador",
          description: "Cadastro rápido com campos essenciais"
        };
      case 'SECRETARIA':
        return {
          showOptionalFields: true,
          enableDocumentValidation: true,
          showComplianceChecks: true,
          showProgressBar: false,
          title: "Revisão de Cliente - Secretaria",
          description: "Validação completa de dados e documentos"
        };
      case 'ASSOCIADO':
      case 'ADMIN':
      case 'SUPER_ADMIN':
      case 'SUPERVISOR':
        return {
          showOptionalFields: true,
          enableQuickActions: true,
          focusOnEssentials: false,
          showProgressBar: true,
          title: "Novo Cliente - Administrador",
          description: "Acesso completo a todas as funcionalidades"
        };
      default:
        return {
          showOptionalFields: true,
          enableQuickActions: false,
          focusOnEssentials: false,
          showProgressBar: false,
          title: "Novo Cliente",
          description: "Preencha os dados para cadastrar um novo cliente"
        };
    }
  };

  return {
    form,
    onSubmit,
    onSaveDraft,
    onSaveAndCreateContract,
    isSubmitting,
    isSavingDraft,
    isSavingAndCreating,
    // Funcionalidades inteligentes
    handleCPFSearch,
    handleFieldValidation,
    dadosCliente,
    loadingCPF,
    erros,
    sugestoes,
    currentRole,
    formConfig: getFormConfig()
  };
}; 